/**
 * Vollbild-Modal für Account-Einstellungen.
 * Zeigt Avatar, Accountname (read-only), Anzeigename, E-Mail, Passwort.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SupabaseClient } from '../network/SupabaseClient';
import { fontSm, fontBase, fontXl, padMd, gapSm, gapMd, radius } from './UITheme';
import { AccountAvatarGrid, renderAvatarImage } from './AccountAvatarGrid';
import { startFieldEdit, startPasswordEdit, makeEditBtn } from './AccountPanelEditors';
import type { FieldEditContext } from './AccountPanelEditors';

const BG = 'rgba(6,14,32,0.97)';
const CARD_BG = 'linear-gradient(180deg,rgba(12,28,60,0.95),rgba(6,16,38,0.95))';
const BRD = 'rgba(100,180,255,0.2)';
const ACCENT = '#8cf';
const MUTED = 'rgba(100,180,255,0.45)';
const ERR_CLR = '#f88';
const OK_CLR = '#8f8';

export interface AccountPanelData {
  email: string;
  displayName: string;
  accountName: string;
  playerId: string;
  avatarId: number;
}

export class AccountPanel implements GameModule {
  readonly name = 'AccountPanel';
  private _overlay!: HTMLDivElement;
  private _card!: HTMLDivElement;
  private _sb: SupabaseClient;
  private _data: AccountPanelData;
  private _avatarEl!: HTMLDivElement;
  private _statusEl!: HTMLDivElement;
  private _ctx!: FieldEditContext;
  private _engine!: Engine;

  constructor(supabase: SupabaseClient, data: AccountPanelData) {
    this._sb = supabase;
    this._data = { ...data };
  }

  init(engine: Engine): void {
    this._engine = engine;
    this._ctx = {
      supabase: this._sb, engine, data: this._data,
      showStatus: (m, c) => this._showStatus(m, c),
    };
    this._overlay = document.createElement('div');
    Object.assign(this._overlay.style, {
      position: 'fixed', inset: '0', zIndex: '50000', display: 'none',
      background: BG, alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace',
    });
    this._card = this._buildCard();
    this._overlay.appendChild(this._card);
    document.body.appendChild(this._overlay);
    engine.events.on('account:open', () => this._show());
  }

  update(): void {}
  dispose(): void { this._overlay.remove(); }

  private _show(): void {
    this._overlay.style.display = 'flex';
    this._card.animate(
      [{ opacity: 0, transform: 'scale(0.95)' }, { opacity: 1, transform: 'scale(1)' }],
      { duration: 200, easing: 'ease-out', fill: 'forwards' },
    );
  }

  private _hide(): void {
    const a = this._card.animate(
      [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.95)' }],
      { duration: 150, easing: 'ease-in', fill: 'forwards' },
    );
    a.onfinish = () => { this._overlay.style.display = 'none'; };
  }

  private _buildCard(): HTMLDivElement {
    const card = document.createElement('div');
    Object.assign(card.style, {
      background: CARD_BG, border: `1px solid ${BRD}`, borderRadius: radius(),
      boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 20px rgba(80,160,255,0.06)',
      width: 'clamp(260px, 80vw, 380px)', maxHeight: '90vh', overflowY: 'auto',
      padding: padMd(), display: 'flex', flexDirection: 'column', gap: gapMd(),
    });
    card.addEventListener('click', (e) => e.stopPropagation());
    this._overlay.addEventListener('click', () => this._hide());
    card.append(
      this._buildHeader(), this._buildAvatarSection(),
      this._readonlyField('Accountname', this._data.accountName),
      this._editableField('Anzeigename', this._data.displayName, 'displayName'),
      this._editableField('E-Mail', this._data.email, 'email'),
      this._passwordField(),
    );
    this._statusEl = document.createElement('div');
    Object.assign(this._statusEl.style, { fontSize: fontSm(), textAlign: 'center', minHeight: '1.2em' });
    card.appendChild(this._statusEl);
    return card;
  }

  private _buildHeader(): HTMLDivElement {
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', justifyContent: 'space-between', alignItems: 'center' });
    const title = Object.assign(document.createElement('span'), { textContent: 'Account' });
    Object.assign(title.style, { fontSize: fontXl(), color: ACCENT, fontWeight: '700' });
    const close = Object.assign(document.createElement('span'), { textContent: '✕' });
    Object.assign(close.style, { fontSize: fontXl(), color: MUTED, cursor: 'pointer', padding: '4px 8px' });
    close.addEventListener('click', () => this._hide());
    row.append(title, close);
    return row;
  }

  private _buildAvatarSection(): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: gapSm() });
    this._avatarEl = renderAvatarImage(this._data.avatarId, 'clamp(56px, 16vw, 80px)');
    this._avatarEl.style.cursor = 'pointer';
    this._avatarEl.addEventListener('click', () => this._openAvatarPicker());
    const label = Object.assign(document.createElement('span'), { textContent: 'Profilbild ändern' });
    Object.assign(label.style, { fontSize: fontSm(), color: MUTED, cursor: 'pointer' });
    label.addEventListener('click', () => this._openAvatarPicker());
    wrap.append(this._avatarEl, label);
    return wrap;
  }

  private _openAvatarPicker(): void {
    const grid = new AccountAvatarGrid(this._data.avatarId, async (id) => {
      grid.remove();
      this._data.avatarId = id;
      const newAv = renderAvatarImage(id, 'clamp(56px, 16vw, 80px)');
      newAv.style.cursor = 'pointer';
      newAv.addEventListener('click', () => this._openAvatarPicker());
      this._avatarEl.replaceWith(newAv);
      this._avatarEl = newAv;
      this._engine.events.emit('account:avatar-changed', { avatarId: id });
      await this._saveAvatar();
    }, () => grid.remove());
    document.body.appendChild(grid.el);
  }

  private _readonlyField(label: string, value: string): HTMLDivElement {
    const sec = document.createElement('div');
    Object.assign(sec.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
    const lbl = Object.assign(document.createElement('div'), { textContent: label });
    Object.assign(lbl.style, { fontSize: fontSm(), color: MUTED, textTransform: 'uppercase', letterSpacing: '1px' });
    const val = Object.assign(document.createElement('span'), { textContent: value });
    Object.assign(val.style, { fontSize: fontBase(), color: 'rgba(180,200,220,0.6)' });
    sec.append(lbl, val);
    return sec;
  }

  private _editableField(label: string, value: string, key: string): HTMLDivElement {
    const sec = document.createElement('div');
    Object.assign(sec.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
    const lbl = Object.assign(document.createElement('div'), { textContent: label });
    Object.assign(lbl.style, { fontSize: fontSm(), color: MUTED, textTransform: 'uppercase', letterSpacing: '1px' });
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: gapSm() });
    const valEl = Object.assign(document.createElement('span'), { textContent: value });
    Object.assign(valEl.style, { fontSize: fontBase(), color: '#cde', flex: '1', wordBreak: 'break-all' });
    row.append(valEl, makeEditBtn(() => startFieldEdit(this._ctx, sec, row, valEl, key, label)));
    sec.append(lbl, row);
    return sec;
  }

  private _passwordField(): HTMLDivElement {
    const sec = document.createElement('div');
    Object.assign(sec.style, { display: 'flex', flexDirection: 'column', gap: '2px' });
    const lbl = Object.assign(document.createElement('div'), { textContent: 'Passwort' });
    Object.assign(lbl.style, { fontSize: fontSm(), color: MUTED, textTransform: 'uppercase', letterSpacing: '1px' });
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: gapSm() });
    const valEl = Object.assign(document.createElement('span'), { textContent: '••••••••' });
    Object.assign(valEl.style, { fontSize: fontBase(), color: '#cde', flex: '1' });
    row.append(valEl, makeEditBtn(() => startPasswordEdit(this._ctx, sec, row)));
    sec.append(lbl, row);
    return sec;
  }

  private async _saveAvatar(): Promise<void> {
    try {
      const res = await this._sb.functions.invoke('update-profile', {
        body: { avatar_id: this._data.avatarId },
      });
      const data = res.data as Record<string, unknown> | null;
      if (res.error) { this._showStatus(`Fehler: ${res.error.message}`, ERR_CLR); return; }
      if (data?.['error']) { this._showStatus(data['error'] as string, ERR_CLR); return; }
      this._showStatus('Avatar geändert', OK_CLR);
    } catch (e) { this._showStatus('Netzwerkfehler', ERR_CLR); }
  }

  private _showStatus(msg: string, color: string): void {
    this._statusEl.textContent = msg; this._statusEl.style.color = color;
    setTimeout(() => { this._statusEl.textContent = ''; }, 3000);
  }
}
